<template>
  <div class="main-content">
    <div class="material-list">
      <div
        v-for="(category, categoryKey) in materialList"
        :key="categoryKey"
      >
        <!-- 分类标题（可点击展开/收起） -->
        <div
          class="category-header"
          :class="{ 'category-expanded': isCategoryExpanded(categoryKey) }"
          @click="toggleCategory(categoryKey)"
        >
          <div class="category-arrow">
            <img
              :src="
                isCategoryExpanded(categoryKey)
                  ? require('@/assets/images/chatpreview/arrow-down.svg')
                  : require('@/assets/images/chatpreview/arrow-right.svg')
              "
              alt="arrow"
            />
          </div>
          <div class="category-label">{{ category.label }}</div>
        </div>
        <!-- 分类下的子项列表 -->
        <div class="category-items" v-show="isCategoryExpanded(categoryKey)">
          <div
            v-for="(item, itemKey) in getCategoryItems(category)"
            :key="itemKey"
            class="material-item"
          >
            <div
              class="item-status-dot"
              :class="getStatusClass(item.value)"
            ></div>
            <div class="item-label">{{ item.label }}</div>
            <div
              class="item-help"
              @mouseleave="hideHelp(categoryKey, itemKey)"
            >
              <img
                src="@/assets/images/chatpreview/material_list_doubt.svg"
                alt="help"
                class="item-help-img"
                @click.stop="showHelp(categoryKey, itemKey)"
              />
              <!-- 帮助提示框 -->
              <div
                v-show="showHelpItemKey === `${categoryKey}-${itemKey}`"
                class="help-tooltip"
                :class="getTooltipPositionClass(categoryKey, itemKey)"
                ref="tooltip"
              >
                {{ item.hint }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "MaterialList",
  props: {
    previewData: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return {
      expandedCategories: [], // 默认不展开任何分类
      showHelpItemKey: null, // 当前显示的帮助项 key
      tooltipPosition: { horizontal: 'left', vertical: 'bottom' }, // 提示框位置
    };
  },
  computed: {
    materialList() {
      // 如果 previewData 存在，优先使用预览数据，否则使用空对象
      if (this.previewData) {
        return this.previewData;
      }
      return {};
    },
  },
  methods: {
    // 获取分类下的子项（排除 label 属性）
    getCategoryItems(category) {
      const items = [];
      for (const key in category) {
        if (key !== "label" && typeof category[key] === "object") {
          items.push({
            key,
            ...category[key],
          });
        }
      }
      return items;
    },
    // 判断分类是否展开
    isCategoryExpanded(categoryKey) {
      return this.expandedCategories.includes(categoryKey);
    },
    // 切换分类展开/收起
    toggleCategory(categoryKey) {
      const index = this.expandedCategories.indexOf(categoryKey);
      if (index > -1) {
        this.expandedCategories.splice(index, 1);
      } else {
        this.expandedCategories.push(categoryKey);
      }
    },
    // 获取状态样式类
    getStatusClass(status) {
      // success -> 绿色
      // error -> 红色
      // pending -> 灰色
      switch (status) {
        case "success":
          return "status-success";
        case "error":
          return "status-error";
        case "pending":
          return "status-pending";
      }
      return "status-pending";
    },
    // 显示帮助信息
    showHelp(categoryKey, itemKey) {
      // 生成唯一标识符：categoryKey-itemKey
      const uniqueKey = `${categoryKey}-${itemKey}`;
      
      // 如果点击的是当前显示的项，则隐藏；否则显示
      if (this.showHelpItemKey === uniqueKey) {
        this.showHelpItemKey = null;
        // 重置位置状态
        this.tooltipPosition = { horizontal: 'left', vertical: 'bottom' };
      } else {
        // 先重置位置状态为默认值
        this.tooltipPosition = { horizontal: 'left', vertical: 'bottom' };
        // 显示新的提示框
        this.showHelpItemKey = uniqueKey;
        // 延迟计算位置，确保DOM已更新
        this.$nextTick(() => {
          this.calculateTooltipPosition(categoryKey, itemKey);
        });
      }
    },
    // 隐藏帮助信息
    hideHelp(categoryKey, itemKey) {
      // 生成唯一标识符：categoryKey-itemKey
      const uniqueKey = `${categoryKey}-${itemKey}`;
      
      // 只有当鼠标离开的是当前显示的项时，才隐藏
      if (this.showHelpItemKey === uniqueKey) {
        this.showHelpItemKey = null;
        // 重置位置状态
        this.tooltipPosition = { horizontal: 'left', vertical: 'bottom' };
      }
    },
    // 计算提示框位置
    calculateTooltipPosition(categoryKey, itemKey) {
      const uniqueKey = `${categoryKey}-${itemKey}`;
      if (this.showHelpItemKey !== uniqueKey) return;
      
      // 再次检查是否还需要显示这个提示框
      if (this.showHelpItemKey !== uniqueKey) return;
      
      // 获取当前显示的提示框元素
      const tooltipElements = this.$refs.tooltip;
      if (!tooltipElements) return;
      
      // 如果是数组，找到对应的元素；如果是单个元素，直接使用
      let tooltipElement = tooltipElements;
      if (Array.isArray(tooltipElements)) {
        // 通过DOM查找当前显示的提示框
        tooltipElement = tooltipElements.find(el => el && el.offsetParent !== null);
      }
      
      if (!tooltipElement) return;
      
      // 获取提示框的边界信息
      const tooltipRect = tooltipElement.getBoundingClientRect();
      const containerRect = this.$el.getBoundingClientRect();
      
      // 检查水平右方向是否超出边界
      const isOverflowRight = tooltipRect.right > containerRect.right;
      
      // 检查垂直下方向是否超出边界
      const isOverflowBottom = tooltipRect.bottom > containerRect.bottom;
      
      // 根据边界情况设置位置
      let horizontal = 'left';
      if (isOverflowRight) {
        horizontal = 'right';
      }

      let vertical = 'bottom';
      if (isOverflowBottom) {
        vertical = 'top';
      }
      
      this.tooltipPosition = { horizontal, vertical };
    },
    // 获取提示框位置类名
    getTooltipPositionClass(categoryKey, itemKey) {
      const uniqueKey = `${categoryKey}-${itemKey}`;
      if (this.showHelpItemKey === uniqueKey) {
        return `tooltip-${this.tooltipPosition.horizontal} tooltip-${this.tooltipPosition.vertical}`;
      }
      return '';
    }
  },
};
</script>

<style scoped lang="scss">
.main-content {
  width: 100%;
  min-height: 269px;
  max-height: 473px;
}

.material-list {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 12px;
}

.category-header {
  width: 100%;
  height: 40px;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 8px;
  padding-left: 10px;

  &:hover {
    background: rgba(244, 248, 252, 1);
  }
}

.category-header.category-expanded {
  background: rgba(219, 232, 245, 1);
}

.category-arrow {
  width: 12px;
  height: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  flex-shrink: 0;
  transition: transform 0.3s ease;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}

.category-label {
  flex: 1;
  font-size: 14px;
  font-weight: 400;
  line-height: 24px;
  color: rgba(0, 43, 37, 1);

  .category-header:not(.category-expanded) & {
    color: rgba(170, 179, 187, 1);
  }
}

.category-items {
  width: 100%;
  padding: 0 10px 0 36px;
  animation: slideDown 0.3s ease;
}

.material-item {
  width: 100%;
  font-size: 14px;
  font-weight: 700;
  display: flex;
  align-items: center;
  padding: 8px 0 8px 0;
  border-bottom: 1px solid rgba(227, 230, 232, 1);
  position: relative;

  &:last-child {
    border-bottom: none;
  }

  &:hover .item-help-img {
    opacity: 1;
  }
}

.item-status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 12px;
  flex-shrink: 0;
}

.item-status-dot.status-success {
  background: rgba(34, 197, 94, 1); // 绿色
}

.item-status-dot.status-error {
  background: rgba(239, 68, 68, 1); // 红色
}

.item-status-dot.status-pending {
  background: rgba(198, 204, 210, 1); // 灰色
}

.item-label {
  margin-right: 10px;
  font-size: 14px;
  line-height: 20px;
  color: rgba(57, 67, 66, 1);
}

.item-help {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: relative;
}

.item-help-img {
  width: 18px;
  height: 18px;
  opacity: 0; // 默认隐藏
  object-fit: contain;

  &:hover {
    filter: brightness(0) saturate(100%);
  }
}

.help-tooltip {
  min-width: 200px;
  max-width: 380px;
  position: absolute;
  font-size: 10px;
  color: rgba(5, 32, 49, 1);
  z-index: 1000;
  text-align: justify;
  line-height: 1.5;
  padding: 8px 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border: 1px solid rgb(0, 0, 0);
  border-radius: 6px;
  background: rgba(255, 255, 255, 1);
  word-wrap: break-word;
  white-space: normal;
  transition: all 0.2s ease;
}

/* 水平位置 */
.help-tooltip.tooltip-left {
  left: 0;
}

.help-tooltip.tooltip-right {
  right: 0;
}

/* 垂直位置 */
.help-tooltip.tooltip-bottom {
  top: 100%;
}

.help-tooltip.tooltip-top {
  bottom: 100%;
}

@keyframes slideDown {
  from {
    opacity: 0;
    max-height: 0;
  }
  to {
    opacity: 1;
    max-height: 1000px;
  }
}
</style>
